import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Barbell, ChatCircleDots, ChartLineUp, GraduationCap, CalendarBlank, Heartbeat, Users, ArrowRight } from "@phosphor-icons/react";

const HERO = "https://images.unsplash.com/photo-1676655079738-af54dfd6318e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwyfHxhdGhsZXRlJTIwdHJhaW5pbmclMjBkYXJrfGVufDB8fHx8MTc4MjUwOTM0MHww&ixlib=rb-4.1.0&q=85";

const features = [
  { icon: Users, title: "Every Role Connected", desc: "Athletic directors, coaches, teachers, athletes and parents — one shared hub." },
  { icon: ChatCircleDots, title: "Messaging & Alerts", desc: "Direct messages plus team and school-wide announcements that reach the right people." },
  { icon: GraduationCap, title: "Grades & Attendance", desc: "Teachers log results; athletes and parents track trends in real time." },
  { icon: CalendarBlank, title: "Games & Practices", desc: "Full team schedules so nobody misses a game, practice or away trip." },
  { icon: Heartbeat, title: "Athlete Wellness", desc: "Athletes log sleep, water and AI-tracked nutrition to perform at their best." },
  { icon: ChartLineUp, title: "Personalized Views", desc: "Each person sees exactly what matters to them — nothing more, nothing less." },
];

const roles = ["Athletic Directors", "Coaches", "Teachers", "Athletes", "Parents"];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="flex items-center justify-between px-6 md:px-12 h-20 border-b border-white/10 sticky top-0 z-40 bg-black/60 backdrop-blur-xl">
        <div className="flex items-center gap-2">
          <Barbell size={28} weight="fill" color="#FF3B30" />
          <span className="font-head text-2xl font-extrabold uppercase tracking-tight">Athlete<span className="text-[#FF3B30]">Hub</span></span>
        </div>
        <Link to="/login" data-testid="nav-login-link" className="bg-[#FF3B30] hover:bg-[#FF5C53] text-white font-bold uppercase tracking-widest text-sm px-6 py-3 rounded-sm transition-colors">Sign in</Link>
      </header>

      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/80 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] to-transparent" />
        </div>
        <div className="relative px-6 md:px-12 py-24 md:py-36 max-w-4xl">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 border border-[#FF3B30]/40 text-[#FF3B30] text-xs font-semibold uppercase tracking-[0.2em] px-4 py-2 rounded-sm mb-6">
              <Users size={14} weight="fill" /> The School Athletics Hub
            </div>
            <h1 className="font-head text-5xl md:text-7xl font-black uppercase tracking-tighter leading-[0.9] mb-6">
              One team.<br /><span className="text-[#FF3B30]">One hub.</span>
            </h1>
            <p className="text-lg md:text-xl text-[#A3A3A3] max-w-xl mb-10 leading-relaxed">
              Connect your whole athletic program. Schedules, grades, attendance, wellness and messaging — personalized for every role.
            </p>
            <Link to="/login" data-testid="hero-cta-button" className="group inline-flex items-center justify-center gap-2 bg-[#FF3B30] hover:bg-[#FF5C53] text-white font-bold uppercase tracking-widest px-8 py-4 rounded-sm transition-colors">
              Enter the hub <ArrowRight size={18} weight="bold" className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>

      <section className="px-6 md:px-12 py-10 border-y border-white/10">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-center">
          <span className="text-xs uppercase tracking-[0.2em] text-[#737373] font-semibold">Built for</span>
          {roles.map((r) => <span key={r} className="font-head text-lg md:text-xl font-bold uppercase tracking-tight text-white/80">{r}</span>)}
        </div>
      </section>

      <section className="px-6 md:px-12 py-20 md:py-28 max-w-7xl mx-auto">
        <h2 className="font-head text-3xl md:text-5xl font-black uppercase tracking-tight mb-3">Everything, <span className="text-[#FF3B30]">in sync.</span></h2>
        <p className="text-[#A3A3A3] max-w-xl mb-14">A single command center for your entire athletic community.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {features.map((f, i) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
              className="bg-[#141414] border border-white/10 rounded-sm p-8 hover:scale-[1.02] hover:border-[#FF3B30]/40 transition-all duration-200">
              <f.icon size={36} weight="duotone" color="#FF3B30" className="mb-5" />
              <h3 className="font-head text-xl font-bold uppercase tracking-tight mb-2">{f.title}</h3>
              <p className="text-sm text-[#A3A3A3] leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="px-6 md:px-12 pb-28">
        <div className="max-w-7xl mx-auto bg-[#FF3B30] rounded-sm px-8 md:px-16 py-16 md:py-20 flex flex-col items-start">
          <h2 className="font-head text-4xl md:text-6xl font-black uppercase tracking-tighter text-white leading-none mb-4">Bring your program together.</h2>
          <p className="text-white/90 text-lg max-w-lg mb-8">Sign in to your personalized AthleteHub dashboard.</p>
          <Link to="/login" data-testid="cta-bottom-button" className="bg-black hover:bg-[#141414] text-white font-bold uppercase tracking-widest px-10 py-4 rounded-sm transition-colors">Sign in</Link>
        </div>
      </section>

      <footer className="border-t border-white/10 px-6 md:px-12 py-8 text-sm text-[#737373] flex items-center justify-between flex-wrap gap-2">
        <span className="font-head font-bold uppercase tracking-tight text-white">Athlete<span className="text-[#FF3B30]">Hub</span></span>
        <span>© 2026 AthleteHub. Connect. Compete. Succeed.</span>
      </footer>
    </div>
  );
}
